import { environment } from "src/environments/environment";

export const AI_LIVE_TAG = "ai_live";

/** LBP `service_type` srv_id short codes for the two LEXI-family types split by LBP-330. */
export const AI_LIVE_LEXI_SRV_ID = "il";
export const LEXI_ACCESS_SRV_ID = "la";

export const DIRECT_MICROPHONE_AUDIO_SOURCE_NAME = "Direct Microphone Input";

/** Ai-Live infra / `creator_location` keys (US, AU, UK stacks). */
export type AiLiveLocationKey = "us" | "au" | "uk";

/**
 * SAM commercial regions that may use LEXI meeting-bot audio selection (Zoom / Meet / Teams / Direct Mic).
 */
export const AI_LIVE_LEXI_MEETING_BOT_AUDIO_SELECTION_ALLOWED_LOCATIONS: readonly AiLiveLocationKey[] = [
  "us",
  "uk",
  "au",
];

/** SAM `region_name` (from `getRegions`) → Ai-Live location key. Includes legacy names still seen in data. */
export const SAM_REGION_NAME_TO_AI_LIVE_LOCATION: Record<string, AiLiveLocationKey> = {
  AMERICAS: "us",
  Americas: "us",
  APAC: "au",
  Asia: "au",
  EMEA: "uk",
};

/**
 * “Meeting bot” audio sources for Ai-Live LEXI — the only options on the booking audio
 * step when meeting-bot audio selection is enabled (env flag + allowed SAM region).
 * This label does not apply to the whole LEXI service type, Instant LEXI, RTMP, etc.
 *
 * Recall.ai is separate: org `recall_ai` / `recall_ai_msg` is a chat disclaimer flag,
 * not an audio source (see organisation settings; passed to SAM on session create).
 */
export const AI_LIVE_LEXI_SELECTABLE_AUDIO_SOURCE_NAMES = [
  "Direct Microphone Input",
  "Google Meet",
  "Microsoft Teams Live",
  "Zoom",
] as const;

/**
 * Subset of meeting-bot sources that use a meeting URL (Zoom / Meet / Teams): overlap
 * checks (LBP-185), two-hour duration cap, integrated-audio UX. Excludes Direct Microphone.
 */
export const AI_LIVE_LEXI_MEETING_PLATFORM_AUDIO_SOURCE_NAMES = [
  "Google Meet",
  "Microsoft Teams Live",
  "Zoom",
] as const;

export type AiLiveLexiSelectableAudioSourceName = (typeof AI_LIVE_LEXI_SELECTABLE_AUDIO_SOURCE_NAMES)[number];
export type AiLiveLexiMeetingPlatformAudioSourceName =
  (typeof AI_LIVE_LEXI_MEETING_PLATFORM_AUDIO_SOURCE_NAMES)[number];

export function isAiLiveLexiSelectableAudioSourceName(audioName: string | undefined | null): boolean {
  return !!audioName && (AI_LIVE_LEXI_SELECTABLE_AUDIO_SOURCE_NAMES as readonly string[]).includes(audioName);
}

export function isAiLiveLexiMeetingPlatformAudioSourceName(audioName: string | undefined | null): boolean {
  return !!audioName && (AI_LIVE_LEXI_MEETING_PLATFORM_AUDIO_SOURCE_NAMES as readonly string[]).includes(audioName);
}

/** LBP-330: "LEXI Access" is matched by name where srv_id isn't available yet (e.g. before the service-type list loads). */
export function isLexiAccessServiceTypeName(name: string | undefined | null): boolean {
  return (name || "").toLowerCase() === "lexi access";
}

export function resolveAiLiveLocationFromSamRegionName(
  regionName: string | undefined | null,
): AiLiveLocationKey | null {
  if (!regionName) {
    return null;
  }
  const trimmed = regionName.trim();
  const direct = SAM_REGION_NAME_TO_AI_LIVE_LOCATION[trimmed];
  if (direct) {
    return direct;
  }
  const upper = trimmed.toUpperCase();
  if (upper === "AMERICAS") {
    return "us";
  }
  if (upper === "APAC" || upper === "ASIA") {
    return "au";
  }
  if (upper === "EMEA") {
    return "uk";
  }
  return null;
}

export function resolveAiLiveLocationFromRegionId(
  regionId: number | undefined | null,
  regions: ReadonlyArray<{ region_id: number; region_name: string }>,
): AiLiveLocationKey | null {
  if (regionId == null || !regions.length) {
    return null;
  }
  const found = regions.find((r) => r.region_id === regionId);
  return found ? resolveAiLiveLocationFromSamRegionName(found.region_name) : null;
}

/**
 * LBP-330: Ai-Live LEXI is unconditionally Direct Microphone Input now, so any legacy stored
 * audio_name of "Ai-Live LEXI" should always display as "Direct Microphone Input", in every region.
 */
export function normalizeLexiAudioName(audioName: string): string {
  if (audioName === "Ai-Live LEXI") {
    return DIRECT_MICROPHONE_AUDIO_SOURCE_NAME;
  }
  return audioName;
}

export function isAiLiveLexiMeetingBotAudioSelectionEnabled(opts?: {
  regionName?: string | null;
  aiLiveLocation?: AiLiveLocationKey | string | null;
  regionId?: number | null;
  regions?: ReadonlyArray<{ region_id: number; region_name: string }>;
}): boolean {
  if (!environment.enable_ai_live_lexi_audio_selection) {
    return false;
  }
  let location: AiLiveLocationKey | null = null;
  if (opts?.aiLiveLocation) {
    location = opts.aiLiveLocation as AiLiveLocationKey;
  } else if (opts?.regionName) {
    location = resolveAiLiveLocationFromSamRegionName(opts.regionName);
  } else if (opts?.regionId != null && opts?.regions?.length) {
    location = resolveAiLiveLocationFromRegionId(opts.regionId, opts.regions);
  }
  if (!location) {
    return false;
  }
  return (AI_LIVE_LEXI_MEETING_BOT_AUDIO_SELECTION_ALLOWED_LOCATIONS as readonly string[]).includes(location);
}
